import React, {
  FC,
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
} from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { PageContext } from '@agria/theme-brochureware/src/context/pageContext';
import type { PageProps } from 'gatsby';
import type { BrandName } from 'packages/types/brand-name';
import { Logout } from '@agria/theme/src/components/icon';
import { useBackgroundColor } from '@agria/theme/src/utils';
import { Button } from '@agria/theme/src/components/button';
import { getQuoteUrlByBrand } from '@agria/theme/src/utils/getByBrand';
import { ExperimentsContext } from '../../../context/ExperimentsContext';
import { TopLevelNav } from '../topLevelNav/TopLevelNav';
import { BottomLevelNav } from '../bottomLevelNav/BottomLevelNav';
import { MobileNav } from '../mobileNav/MobileNav';
import tokens from './tokens';

type HeaderLink = {
  label: string;
  target: string | null;
  url: string;
};

export interface HeaderProps {
  brand?: BrandName;
  ctaLink?: string;
  ctaText?: string;
  enableMyAccount?: boolean;
  isMyAccount?: boolean; // Set to true when within my-account routes
  loggedIn?: boolean; // Set to true when user is in my account context
  myAccountLogout?: () => void; // Function to logout user
  navigationItems: NavigationItem[];
  secondaryNavigationItems: SecondaryNavigationItem[];
  siteId?: number;
  location?: PageProps['location'];
  showSearch: boolean;
}

export interface NavigationItem {
  ctaOverride: HeaderLink | null;
  headerChildItems?: HeaderChildItems[];
  hideTelephoneNumber: boolean;
  hideTopLevelNavigation: boolean;
  link: HeaderLink;
  telephoneNumberOverride?: string;
}

export interface HeaderChildItems {
  link: HeaderLink;
  navigationChildItems: SecondaryNavigationItem[];
}

export interface SecondaryNavigationItem {
  link: HeaderLink;
}

const StyledHeader = styled.header<{ $AGRIA146: boolean }>`
  ${({ theme: { layers }, $AGRIA146 }) => css`
    ${useBackgroundColor(tokens.backgroundColor || 'utilityWhite')};

    ${
      $AGRIA146 &&
      css`
        position: sticky;
        top: 0;
        z-index: ${layers.blade};
        animation: agria146-header-shadow ease-out both;
        animation-timeline: scroll(root block);
        animation-range: 0 128px;

        @keyframes agria146-header-shadow {
          from {
            box-shadow: 0 4px 32px rgba(0, 0, 0, 0);
          }

          to {
            box-shadow: 0 4px 32px rgba(0, 0, 0, 0.08);
          }
        }
      `
    }
  `};
`;

const StyledStickyCta = styled.div<{ show: boolean }>`
  ${({ theme: { colors, space, media, layers }, show }) => css`
    position: fixed;
    bottom: 0;
    width: 100%;
    padding: ${space.small} ${space.medium};
    display: grid;
    background-color: ${colors.utilityWhite};
    z-index: ${layers.stickyCTA};
    box-shadow: 0 0 12px -3px rgba(0, 0, 0, 0.4);
    transition: transform 0.3s ease-in-out;
    transform: ${show ? 'translateY(0)' : 'translateY(100%)'};

    @media (min-width: ${media.large}) {
      display: none;
    }
  `};
`;

const findTopLevelNavItem = (
  navItems: NavigationItem[],
  currentPath: string | undefined,
) => {
  if (!navItems || !currentPath) return null;

  // Remove any trailing slashes from the currentPath
  const cleanPath = currentPath.replace(/\/$/, '');

  // If the path is '/', return the first item
  if (cleanPath === '' && navItems.length > 0) {
    return navItems[0] || null;
  }

  // Split the path into parts using '/'
  const pathParts = cleanPath.split('/');

  // The first part should be what you're looking for
  const topLevelLink = `/${pathParts[1]}/`;

  // Find the top-level navigation item with a matching link
  const activePage = navItems?.find((item) => item.link.url === topLevelLink);

  if (!activePage) {
    return navItems[0] || null;
  }
  return activePage;
};

export const Header: FC<HeaderProps> = ({
  brand,
  siteId,
  navigationItems,
  secondaryNavigationItems,
  enableMyAccount,
  myAccountLogout,
  loggedIn,
  isMyAccount,
  ctaText,
  ctaLink,
  location,
  showSearch = false,
}) => {
  const activePage = useMemo(
    () => findTopLevelNavItem(navigationItems, location?.pathname),
    [navigationItems, location?.pathname],
  );
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      lastScrollY.current = window.scrollY;
    }
  }, []);

  const isDualBrand =
    brand === 'BASC' ||
    brand === 'GCCF' ||
    brand === 'RDA' ||
    brand === 'Dog Friendly' ||
    brand === 'Crufts' ||
    brand === 'Insure K9' ||
    brand === 'CTM' ||
    brand === 'Clegg Gifford' ||
    brand === 'DogFest' ||
    brand === 'Pets4Homes';

  const [showStickyCta, setShowStickyCta] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const { experiments } = useContext(ExperimentsContext);

  const { stickyCallToAction, overrideStickyCTAButton } =
    useContext(PageContext)!;

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const headerHeight = headerRef.current
        ? headerRef.current.offsetHeight
        : 0;

      // Determine if the header is out of view
      const headerOutOfView = currentScrollY > headerHeight;

      // Show the CTA if:
      // 1. Scrolling up and past the Header
      // 2. Scrolling down and the Header is out of view
      const shouldShowStickyCta =
        (currentScrollY < lastScrollY.current &&
          currentScrollY > headerHeight) ||
        (currentScrollY > lastScrollY.current && headerOutOfView);

      setShowStickyCta(shouldShowStickyCta);

      // Update the last scroll position
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <StyledHeader
        ref={headerRef}
        $AGRIA146={experiments.AGRIA146 === 'variation'}
      >
        {!isDualBrand && (
          <TopLevelNav
            activePage={activePage}
            enableMyAccount={enableMyAccount}
            isMyAccount={isMyAccount}
            navigationItems={navigationItems}
            secondaryNavigationItems={secondaryNavigationItems}
          />
        )}

        <BottomLevelNav
          activePage={activePage}
          brand={brand}
          ctaLink={ctaLink}
          ctaText={ctaText}
          overrideStickyCTAButton={overrideStickyCTAButton}
          enableMyAccount={enableMyAccount}
          isDualBrand={isDualBrand}
          isMyAccount={isMyAccount}
          location={location}
          loggedIn={loggedIn}
          myAccountLogout={myAccountLogout}
          showSearch={showSearch}
          siteId={siteId}
        />

        <MobileNav
          activePage={activePage}
          brand={brand}
          ctaLink={ctaLink}
          ctaText={ctaText}
          enableMyAccount={enableMyAccount}
          loggedIn={loggedIn}
          myAccountLogout={myAccountLogout}
          navigationItems={navigationItems}
          secondaryNavigationItems={secondaryNavigationItems}
        />
      </StyledHeader>

      {stickyCallToAction !== 'Disabled' && (
        <>
          {isMyAccount && enableMyAccount && loggedIn && (
            <StyledStickyCta show={showStickyCta}>
              <Button
                label="Log out"
                small
                onClick={myAccountLogout}
                variant="secondaryOutline"
                iconLeft={<Logout />}
              />
            </StyledStickyCta>
          )}
          {!isMyAccount && (
            <StyledStickyCta show={showStickyCta}>
              <Button
                url={
                  overrideStickyCTAButton?.url ||
                  activePage?.ctaOverride?.url ||
                  getQuoteUrlByBrand(brand)
                }
                label={
                  overrideStickyCTAButton?.label ||
                  activePage?.ctaOverride?.label ||
                  'Get a quote'
                }
                arrowRight
                small
                target={overrideStickyCTAButton?.target ?? undefined}
              />
            </StyledStickyCta>
          )}
        </>
      )}
    </>
  );
};

export default Header;
